import React, { useContext, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { css } from '@emotion/core'
import { RoomSp } from './room'
import { CleaningManagerContext } from '../room'
import {
  CleaningStatusSelectOrder,
  CleaningStatusType,
  CleaningStatusValues,
  RoomsDataType,
  RoomSortKey,
} from '@/constants/cleaning-manager'
import { Dayjs } from 'dayjs'
import { useHistory } from 'react-router-dom'
import { initializeSelectBoxStyle } from '@/constants/form'
import { buildCleaningRoomList } from '@/libs/cleaning-manager'

interface CleaningListSpProps {
  rooms: RoomsDataType
  selectedDate: Dayjs
  staffId: string
  tabIndex: string
  sortKey: RoomSortKey
  cleaningStatus: CleaningStatusType | undefined
  onChangeCleaningStatus: (cleaningStatus: CleaningStatusType | undefined) => void
}

export const CleaningListSp = ({
  rooms,
  selectedDate,
  staffId,
  tabIndex,
  sortKey,
  cleaningStatus,
  onChangeCleaningStatus,
}: CleaningListSpProps) => {
  const [cleaningStaffId, setCleaningStaffId] = useState(staffId)

  const { t } = useTranslation()

  const { staffList } = useContext(CleaningManagerContext)

  const history = useHistory()

  const onChangeStaff = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const changedStaffId = e.target.value
    setCleaningStaffId(changedStaffId)

    e.preventDefault()
    history.push(`/cleaning/?tabIndex=${tabIndex}&dateOf=${selectedDate.format('YYYY-MM-DD')}&staffId=${changedStaffId ?? ''}`)
  }

  const displayRoomList = useMemo(
    () => buildCleaningRoomList(rooms, selectedDate, { cleaningStaffId, cleaningStatus }, sortKey),
    [rooms, cleaningStaffId, cleaningStatus, sortKey, selectedDate],
  )

  return (
    <div css={guestRoomBlockStyle}>
      <div css={filterBlockStyle}>
        <select css={[selectStyle, !!cleaningStaffId && selectedSelectStyle]} onChange={e => onChangeStaff(e)} value={cleaningStaffId}>
          <option value="">{t('cleaningManager.CleaningStaff')}</option>
          {staffList?.map(staff => (
            <option key={staff.id} value={staff.id}>
              {staff.name}
            </option>
          ))}
        </select>

        <select
          css={[selectStyle, cleaningStatus !== undefined && selectedSelectStyle]}
          onChange={e => onChangeCleaningStatus(e.target.value === '' ? undefined : (Number(e.target.value) as CleaningStatusType))}
          value={cleaningStatus ?? ''}
        >
          <option value="">{t('cleaningManager.Cleaning Status')}</option>
          {CleaningStatusSelectOrder.map(status => (
            <option key={status} value={status}>
              {t(CleaningStatusValues[status])}
            </option>
          ))}
        </select>
      </div>

      {displayRoomList.map(room => (
        <RoomSp key={room.guestRoomId} room={room} cleaningStaffId={cleaningStaffId} />
      ))}
    </div>
  )
}

const guestRoomBlockStyle = css({
  display: 'flex',
  flexDirection: 'column',
  gap: 8,
  padding: 24,
  overflowY: 'auto',
})

const filterBlockStyle = css({
  display: 'flex',
  gap: 8,
  marginBottom: 24,
})

const selectStyle = css(initializeSelectBoxStyle, {
  flexGrow: 1,
  flexBasis: 0,
  minWidth: 0,
  height: 46,
  padding: '0 30px 0 12px',
  color: '#676767',
  backgroundImage: `url(${require('@/static/images/arrow_down_yellow.svg')})`,
  backgroundRepeat: 'no-repeat',
  backgroundPosition: 'right 12px center',
  border: '1px solid #CCCCCC',
  borderRadius: 5,
})

const selectedSelectStyle = css({
  color: '#272727',
  fontWeight: 'bold',
  border: '1px solid #F2A40B',
  backgroundColor: '#FFF8EC',
})
